import { useParams, Navigate } from 'react-router-dom';
import { useTransitionNavigate } from '../components/PageTransition';
import { HelmetAsync } from '../components/HelmetAsync';
import { useReveal } from '../hooks/useReveal';
import { PrimaryButton, GhostButton } from '../components/ui';
import { RelatedCard } from '../components/ProductCard';
import { getProduct, getRelated, productPath } from '../data/products';
import { color, font, maxWidth } from '../styles/tokens';

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useTransitionNavigate();
  const product = getProduct(id);

  // Unknown id -> bounce to the products index.
  useReveal([id]);
  if (!product) return <Navigate to="/products" replace />;

  const related = getRelated(product, 3);

  // Send the visitor to the contact form with this product preselected.
  const enquire = () => navigate('/contact', { state: { requirement: product.name } });

  return (
    <div style={{ animation: 'vt-fade .4s ease' }}>
      <HelmetAsync
        title={`${product.name} — V-Tech Industries`}
        description={product.desc}
        keywords={`${product.name}, ${product.family}, V-Tech Industries`}
        url={productPath(product.id)}
      />

      {/* breadcrumb */}
      <section style={{ background: 'rgba(255,255,255,.015)', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <div style={{ maxWidth, margin: '0 auto', padding: '22px 28px', font: `400 13px ${font.body}`, color: color.textDim }}>
          <button onClick={() => navigate('/products')} style={{ background: 'none', border: 'none', color: color.accent, cursor: 'pointer', font: `500 13px ${font.body}`, padding: 0 }}>
            Products
          </button>
          <span style={{ margin: '0 8px', color: color.textFainter }}>/</span>
          <span>{product.family}</span>
          <span style={{ margin: '0 8px', color: color.textFainter }}>/</span>
          <span style={{ color: color.text, fontWeight: 600 }}>{product.name}</span>
        </div>
      </section>

      {/* main */}
      <section style={{ maxWidth, margin: '0 auto', padding: '60px 28px', display: 'flex', flexWrap: 'wrap', gap: 56 }}>
        {/* media */}
        <div data-reveal style={{ flex: '1 1 400px', minWidth: 300 }}>
          {product.hasImg ? (
            <div style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,.08)', background: '#0b0f17' }}>
              <img src={product.img} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
          ) : (
            <div style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 20, background: 'repeating-linear-gradient(135deg,#151b26 0 16px,#10141d 16px 32px)', border: '1px solid rgba(255,255,255,.08)', overflow: 'hidden' }}>
              <span style={{ position: 'absolute', left: 16, bottom: 16, font: `500 11px ${font.mono}`, textTransform: 'uppercase', color: color.textFaint, background: 'rgba(10,12,18,.85)', border: '1px solid rgba(255,255,255,.08)', padding: '6px 10px', borderRadius: 8 }}>
                product photo · {product.name}
              </span>
            </div>
          )}
          {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginTop: 14 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ aspectRatio: '1', borderRadius: 14, background: 'repeating-linear-gradient(135deg,#151b26 0 12px,#10141d 12px 24px)', border: '1px solid rgba(255,255,255,.08)' }} />
            ))}
          </div> */}
        </div>

        {/* details */}
        <div data-reveal data-delay="100ms" style={{ flex: '1 1 380px', minWidth: 300 }}>
          <span style={{ display: 'inline-block', font: `500 11px ${font.mono}`, letterSpacing: '.08em', color: color.accent, background: color.accentBg, border: `1px solid ${color.accentBorder}`, padding: '7px 13px', borderRadius: 999 }}>
            {product.family}
          </span>
          <h1 style={{ margin: '18px 0 0', font: `700 clamp(32px,4.4vw,52px)/1.02 ${font.display}`, letterSpacing: '-.03em', color: color.text }}>
            {product.name}
          </h1>
          <p style={{ margin: '18px 0 0', font: `400 16px/1.7 ${font.body}`, color: color.textMuted }}>{product.desc}</p>

          {product.order && (
            <div style={{ marginTop: 26, padding: '18px 22px', background: 'rgba(77,141,255,.1)', border: '1px solid rgba(77,141,255,.25)', borderRadius: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <span style={{ font: `500 12px ${font.mono}`, color: color.accentSoft, letterSpacing: '.04em' }}>HOW TO ORDER</span>
              <span style={{ font: `700 18px ${font.mono}`, color: color.text }}>{product.order}</span>
            </div>
          )}

          <div style={{ marginTop: 30 }}>
            <div style={{ font: `500 12px ${font.mono}`, letterSpacing: '.1em', color: color.textFaint, marginBottom: 14 }}>KEY FEATURES</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {product.features.map((ft) => (
                <div key={ft} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', font: `400 14px/1.4 ${font.body}`, color: '#cdd4e0' }}>
                  <span style={{ width: 17, height: 17, flex: 'none', marginTop: 2, borderRadius: 5, background: color.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.bg, font: `700 10px ${font.display}` }}>✓</span>
                  {ft}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 28 }}>
            <div style={{ font: `500 12px ${font.mono}`, letterSpacing: '.1em', color: color.textFaint, marginBottom: 14 }}>APPLICATIONS</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {product.apps.map((ap) => (
                <span key={ap} style={{ font: `400 13px ${font.body}`, color: '#cdd4e0', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.12)', padding: '8px 14px', borderRadius: 999 }}>
                  {ap}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 36 }}>
            <PrimaryButton onClick={enquire} style={{ padding: '16px 28px' }}>Enquire about this product</PrimaryButton>
            <GhostButton magnetic={false} onClick={() => navigate('/products')} style={{ padding: '16px 28px' }}>← All products</GhostButton>
          </div>
        </div>
      </section>

      {/* related */}
      {related.length > 0 && (
        <section style={{ background: 'rgba(255,255,255,.015)', borderTop: '1px solid rgba(255,255,255,.07)' }}>
          <div style={{ maxWidth, margin: '0 auto', padding: '64px 28px' }}>
            <h2 data-reveal style={{ margin: '0 0 26px', font: `600 24px ${font.display}`, color: color.text }}>More in {product.family}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 18 }}>
              {related.map((p) => (
                <RelatedCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
